import { Component, OnInit } from '@angular/core';
import { Logger } from '@utils/logger';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { AppStateService } from '@services/app-state.service';
import { LinkService } from '@services/link.service';
import {
  DynamicBlockCTAButton,
  DynamicCardTheme,
  DynamicCardThemeMap,
  EdsDynamicCardType,
} from './dynamic-cards.type';
import { SiteConfigService } from '@services';
import { WidenDocumentJson } from '@types';
import { ViewModeService } from '@services/view-mode.service';

/**
 * Logger
 */
const logger = Logger.getLogger('DynamicCardsComponent');

// ContentModel based on @bloomreach/spa-sdk.ContentModel. It is not exportable.
interface ContentModel {
  [property: string]: any;
}

/**
 * FT DynamicCardsComponent
 *
 * This component is used at the top of a template and has it's own associated document type.
 *
 * Selector: `ft-dynamic-cards`
 *
 * Exported as: `DynamicCardsComponent`
 */
@Component({
  selector: 'ft-dynamic-cards',
  templateUrl: './dynamic-cards.component.html',
})
export class DynamicCardsComponent
  extends AbstractBaseComponent
  implements OnInit {
  /**
   * Component content
   */
  content: ContentModel;

  // Card theme
  cardTheme: EdsDynamicCardType;

  hasPanelShadow = false;

  // Click To Action button for dynamic block
  ctaButton: DynamicBlockCTAButton;

  /**
   * Constructor
   */
  constructor(
    private appStateService: AppStateService,
    private linkService: LinkService,
    private siteConfigService: SiteConfigService,
    private viewModeService: ViewModeService
  ) {
    super();
    this.isEditMode = this.viewModeService.isEditMode();
  }

  /**
   * Angular init hook
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.content = this.getContent();
    this.assignTheme();
    this.ctaButton = this.createCTAButton(this.content?.buttonBlock);
  }

  /**
   * Get content from document and process the CTA links
   * in the card.
   */
  /**
   * Get content from document
   */
  private getContent(): ContentModel {
    const content = this.document?.getData();
    if (content) {
      // Process Card Links
      for (const card of content?.htmlCards) {
        // WDE-1642 : Australia Campaign Page Callout Tracking Issue
        // instead of HomePageUrl we need only the spaBaseUrl so that
        // the href for dynamic doesn't contain a full url and contains
        // a router redirection so that the page doesn't gets refreshed.
        if (card.link?.linkCollection?.length) {
          card.link = card.link.linkCollection[0];
          card.link.downloadLocation = this.linkService.getDownloadLocationHref(
            this.page,
            card.link,
            this.appStateService?.getSpaBaseUrl()
          );
          card.link.href = this.linkService.getCTALink(
            this.page,
            card.link,
            this.appStateService?.getSpaBaseUrl()
          );
          logger.debug('Card link:', card.link);
        }
        if (card.optionallink?.linkCollection?.length) {
          card.optionallink = card.optionallink.linkCollection[0];
          card.optionallink.downloadLocation = this.linkService.getDownloadLocationHref(
            this.page,
            card.optionallink,
            this.appStateService?.getSpaBaseUrl()
          );
          card.optionallink.href = this.linkService.getCTALink(
            this.page,
            card.optionallink,
            this.appStateService?.getHomePageUrl()
          );
          logger.debug('Optional card link:', card.optionallink);
        } else {
          card.optionallink = null;
        }
        if (
          (card.image && card.image.widenDocument) ||
          (card.logo && card.logo.widenDocument)
        ) {
          card.hasMediaLeft = true;
          let widenParams: WidenDocumentJson;
          if (card.image && card.image.widenDocument) {
            card.imageClass = 'full';
            widenParams = JSON.parse(card.image.widenDocument);
          } else {
            card.imageClass = 'logo';
            widenParams = JSON.parse(card.logo.widenDocument);
          }
          card.imagePath = widenParams?.url;
          card.widenParams = widenParams;
        }
      }
    }
    logger.debug('Card content:', content);
    return content;
  }

  /**
   * Assign theme to Dynamic card
   */
  private assignTheme(): void {
    const { theme } = this.component?.getParameters();
    const mappedTheme = DynamicCardThemeMap.get(theme);
    this.cardTheme =
      mappedTheme || DynamicCardThemeMap.get(DynamicCardTheme.PRIMARY_A);

    if (this.cardTheme === 'white') {
      this.hasPanelShadow = true;
    }
  }

  setDownloadLocation(event: string) {
    this.linkService.setUpdatedLinkData(event);
  }

  /**
   * Creates dynamic block CTA button if data is present on bloomreach.
   * @param brButtonContent bloomreach data for button block containing links and title
   * @returns Formatted button data ready to be consumed by eds dynamic block
   */
  private createCTAButton(brButtonContent): DynamicBlockCTAButton {
    const ctaButton: DynamicBlockCTAButton = {
      displayText: '',
      href: '',
      target: false,
      isExternalLink: false,
    };
    // Internal link
    if (brButtonContent?.linkCompound?.linkCollection[0]?.document?.$ref) {
      const internalLink = this.page.getContent(
        brButtonContent?.linkCompound?.linkCollection[0]?.document?.$ref
      );
      ctaButton.displayText = internalLink?.getName();
      ctaButton.href = internalLink?.getUrl();
      ctaButton.target =
        brButtonContent?.linkCompound?.linkCollection[0]?.target;
      ctaButton.isExternalLink =
        brButtonContent?.linkCompound?.linkCollection[0]?.external;
    }

    // Widen Asset Compound
    if (brButtonContent?.linkCompound?.linkCollection[0]?.widenAssetCompound) {
      const widenAssetDetails = JSON.parse(
        brButtonContent?.linkCompound?.linkCollection[0]?.widenAssetCompound
          ?.widenDocument
      );
      ctaButton.displayText =
        brButtonContent?.linkCompound?.linkCollection[0]?.displayText;
      ctaButton.href = widenAssetDetails?.url;
      ctaButton.target =
        brButtonContent?.linkCompound?.linkCollection[0]?.target;
      ctaButton.isExternalLink =
        brButtonContent?.linkCompound?.linkCollection[0]?.external;
    }

    // Old Dynamic Card type configuration
    if (
      brButtonContent?.linkCompound?.linkCollection[0]?.displayText &&
      brButtonContent?.linkCompound?.linkCollection[0]?.url
    ) {
      ctaButton.displayText =
        brButtonContent?.linkCompound?.linkCollection[0]?.displayText;
      ctaButton.href = brButtonContent?.linkCompound?.linkCollection[0]?.url;
      ctaButton.target =
        brButtonContent?.linkCompound?.linkCollection[0]?.target;
      ctaButton.isExternalLink =
        brButtonContent?.linkCompound?.linkCollection[0]?.external;
    }
    return ctaButton;
  }
}
